import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTenant } from "../../../Contexts/TenantContext";
import { productService } from "../../../Api/Services/productService";
import { reportService } from "../../../Api/Services/reportService";
import { productCategoryService } from "../../../Api/Services/productCategoryService";
import { productBrandService } from "../../../Api/Services/productBrandService";
import { productModelService } from "../../../Api/Services/productModelService";
import { createOrderHubConnection } from "../../../Api/orderHub";
import { ProductMissingAttributesResponse, SimplifiedAdminProduct, UpdateProductBulkRequest } from "../../../Models/Product";
import { ProductCategory } from "../../../Models/ProductCategory";
import { ProductBrand } from "../../../Models/ProductBrand";
import { ProductModel } from "../../../Models/ProductModel";
import { PriceAdjustmentOperation } from "../../../Enums/PriceAdjustmentOperation";
import { ReportFormat } from "../../../Enums/ReportFormat";
import { formatDateTimeToBR, formatPriceBRL } from "../../../Utils/DataFormatters";
import BlockedAction from "../../../Components/BlockedAction/BlockedAction";
import { parseCurrencyInput } from "../../../Utils/InputParsers";
import { Input } from "../../../Components/Input/Input";
import { FaBoxOpen, FaCheck, FaCheckCircle, FaCopy, FaEdit, FaEye, FaFileCsv, FaImages, FaPercentage, FaPlus, FaSearch, FaTimes, FaTimesCircle, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import AdjustPricesModal from "../../../Components/AdjustPricesModal/AdjustPricesModal";
import BulkUpdateProductsModal from "../../../Components/BulkUpdateProductsModal/BulkUpdateProductsModal";
import ProductAnalysisModal from "../../../Components/ProductAnalysisModal/ProductAnalysisModal";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import DeleteProductModal from "../../../Components/DeleteProductModal/DeleteProductModal";
import ReportFormatModal from "../../../Components/ReportFormatModal/ReportFormatModal";
import Pagination from "../../../Components/Pagination/Pagination";
import PaginationSummary from "../../../Components/Pagination/PaginationSummary";
import AdminProductDetailsModal from "../../../Components/AdminProductDetailsModal/AdminProductDetailsModal";
import AdminProductFilesModal from "../../../Components/AdminProductFilesModal/AdminProductFilesModal";
import AdminProductViewModal from "../../../Components/AdminProductViewModal/AdminProductViewModal";
import EditProductSizesModal from "../../../Components/EditProductSizesModal/EditProductSizesModal";
import Button, { ButtonSpinner } from "../../../Components/Button/Button";

const getDeleteBlockedReason = (product: SimplifiedAdminProduct) => {
  if (product.linkedToOrders) {
    return "Este produto não pode ser excluído porque está vinculado a pedidos.";
  }
  if (product.linkedToFeatured) {
    return "Este produto não pode ser excluído porque está vinculado aos destaques.";
  }
  return "Este produto não pode ser excluído.";
};

const AdminProductPage = () => {
  const { tenantPreference } = useTenant();
  const enableBrandsFeature = tenantPreference?.enableBrandsFeature ?? true;
  const enableCategoriesFeature = tenantPreference?.enableCategoriesFeature ?? true;
  const enableModelsFeature = tenantPreference?.enableModelsFeature ?? true;
  const enableMadeToOrderProducts = tenantPreference?.enableMadeToOrderProducts ?? true;
  const navigate = useNavigate();

  const [products, setProducts] = useState<SimplifiedAdminProduct[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [brands, setBrands] = useState<ProductBrand[]>([]);
  const [models, setModels] = useState<ProductModel[]>([]);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [newlyAddedId, setNewlyAddedId] = useState<string | null>(null);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [status, setStatus] = useState<"all" | "active" | "inactive">("all");

  const location = useLocation();
  // ?busca= pre-fills the search, e.g. when opening a notification
  const [search, setSearch] = useState<string>(() => new URLSearchParams(location.search).get("busca") ?? "");

  useEffect(() => {
    const busca = new URLSearchParams(location.search).get("busca");
    if (busca !== null) {
      setSearch(busca);
    }
  }, [location.search]);

  const [detailsModalId, setDetailsModalId] = useState<string | null>(null);
  const [viewModalId, setViewModalId] = useState<string | null>(null);
  const [filesModalId, setFilesModalId] = useState<string | null>(null);

  const [isAdjustPricesModalOpen, setAdjustPricesModalOpen] = useState(false);
  const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false);
  const [isProductAnalysisModalOpen, setIsProductAnalysisModalOpen] = useState(false);
  const [productAnalysis, setProductAnalysis] = useState<ProductMissingAttributesResponse | null>(null);
  const [isAnalyzingProducts, setIsAnalyzingProducts] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftCode, setDraftCode] = useState<string>("");
  const [savingId, setSavingId] = useState<string | null>(null);
  const isCodeEditing = editingId !== null;

  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [savingNameId, setSavingNameId] = useState<string | null>(null);

  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [draftPrice, setDraftPrice] = useState<number | null>(null); // Parsed numeric
  const [draftPriceText, setDraftPriceText] = useState(""); // Formatted string
  const [savingPriceId, setSavingPriceId] = useState<string | null>(null);
  const isPriceEditing = editingPriceId !== null;

  const [editingPlusPriceId, setEditingPlusPriceId] = useState<string | null>(null);
  const [draftPlusPrice, setDraftPlusPrice] = useState<number | null>(null);
  const [draftPlusPriceText, setDraftPlusPriceText] = useState("");
  const [savingPlusPriceId, setSavingPlusPriceId] = useState<string | null>(null);
  const isPlusPriceEditing = editingPlusPriceId !== null;

  const ALL_SIZES = ["P", "M", "G", "GG", "G1", "G2", "G3"];
  const [sizesModalProductId, setSizesModalProductId] = useState<string | null>(null);
  const [draftSizes, setDraftSizes] = useState<string[]>([]);
  const [savingSizes, setSavingSizes] = useState(false);

  const editingSizesProduct = sizesModalProductId ? products.find(p => p.id === sizesModalProductId) ?? null : null;

  const formatSizesDisplay = (sizes: string[] | null | undefined) => {
    if (!sizes || sizes.length === 0 || sizes.length === ALL_SIZES.length) {
      return "P–G3";
    }
    return sizes.join(", ");
  };

  const handleSaveSizes = async () => {
    if (!sizesModalProductId) {
      return;
    }
    setSavingSizes(true);
    try {
      const payload = draftSizes.length === 0 || draftSizes.length === ALL_SIZES.length ? null : draftSizes;
      const updated = await productService.updateAvailableSizes(sizesModalProductId, payload);
      if (updated) {
        toast.success("Tamanhos atualizados.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        setSizesModalProductId(null);
      } else {
        toast.error("Não foi possível atualizar os tamanhos.");
      }
    } catch {
      toast.error("Não foi possível atualizar os tamanhos.");
    } finally {
      setSavingSizes(false);
    }
  };

  const [editingStockId, setEditingStockId] = useState<string | null>(null);
  const [draftStock, setDraftStock] = useState<string>("");
  const [savingStockId, setSavingStockId] = useState<string | null>(null);
  const isStockEditing = editingStockId !== null;

  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [draftCategoryId, setDraftCategoryId] = useState<string>("");
  const [savingCategoryId, setSavingCategoryId] = useState<string | null>(null);
  const isCategoryEditing = editingCategoryId !== null;

  const [editingBrandId, setEditingBrandId] = useState<string | null>(null);
  const [draftBrandId, setDraftBrandId] = useState<string>("");
  const [savingBrandId, setSavingBrandId] = useState<string | null>(null);
  const isBrandEditing = editingBrandId !== null;

  const [editingModelId, setEditingModelId] = useState<string | null>(null);
  const [draftModelId, setDraftModelId] = useState<string>("");
  const [savingModelId, setSavingModelId] = useState<string | null>(null);
  const isModelEditing = editingModelId !== null;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const productsPerPage = 16;
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isReportFormatModalOpen, setIsReportFormatModalOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hubRef = useRef<ReturnType<typeof createOrderHubConnection> | null>(null);

  const handleOpenProductAnalysis = async () => {
    setIsProductAnalysisModalOpen(true);
    setIsAnalyzingProducts(true);
    try {
      const result = await productService.getMissingAttributes();
      setProductAnalysis(result);
    } catch (error) {
      toast.error("Erro ao analisar produtos.");
      setProductAnalysis(null);
    } finally {
      setIsAnalyzingProducts(false);
    }
  };

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [categoriesData, brandsData, modelsData] = await Promise.all([
          productCategoryService.getAll(),
          productBrandService.getAll(),
          productModelService.getAll()
        ]);

        setCategories(categoriesData);
        setBrands(brandsData);
        setModels(modelsData);
      } catch {
        toast.error("Falha para carregar marcas, categorias ou modelos.");
      }
    };

    void loadLookups();
  }, []);

  useEffect(() => {
    startHub();

    return () => {
      if (hubRef.current) {
        hubRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
  }, [currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategoryId, selectedBrandId, selectedModelId, status, search]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const loadProducts = async (
    page: number = 1,
    categoryId?: string | null,
    brandId?: string | null,
    modelId?: string | null,
    status?: string,
    search?: string
  ) => {
    setIsLoadingProducts(true);
    try {
      const data = await productService.getAll({
        page,
        pageSize: productsPerPage,
        categoryId: categoryId ?? null,
        brandId: brandId ?? null,
        modelId: modelId ?? null,
        status,
        search
      });

      setProducts(data.items);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalItems);
      setLastUpdated(new Date());
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleSelectCategory = (id: string | null) => {
    setSelectedCategoryId(id);
  };

  const handleSelectBrand = (id: string | null) => {
    setSelectedBrandId(id);
  };

  const handleSelectModel = (id: string | null) => {
    setSelectedModelId(id);
  };

  const handleSelectStatus = (value: "all" | "active" | "inactive") => {
    setStatus(value);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const refreshProducts = async () => {
    setIsRefreshing(true);
    try {
      await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
    } finally {
      setIsRefreshing(false);
    }
  };

  const startHub = async () => {
    if (hubRef.current) {
      return;
    }

    const hub = createOrderHubConnection();
    hubRef.current = hub;

    hub.on("ProductStockChanged", () => {
      refreshProducts();
    });

    try {
      await hub.start();
    } catch {
      // Ignore hub start errors; manual refresh still works
    }
  };

  const handleGenerateReport = async (format: ReportFormat) => {
    try {
      setIsGeneratingReport(true);
      const report = await reportService.createProductReport({
        categoryId: selectedCategoryId,
        brandId: selectedBrandId,
        modelId: selectedModelId,
        status: status === "all" ? null : status,
        search: search || null,
        format
      });

      toast.success("Relatório gerado. Abrindo download...");
      window.open(report.url, "_blank", "noopener");
    } catch (error) {
      toast.error("Não foi possível gerar o relatório.");
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleOpenReportModal = () => {
    setIsReportFormatModalOpen(true);
  };

  const handleSelectReportFormat = (format: ReportFormat) => {
    setIsReportFormatModalOpen(false);
    handleGenerateReport(format);
  };

  const handleAdjustPrices = async (
    categoryId: string, 
    percentage: number, 
    operation: PriceAdjustmentOperation
  ): Promise<boolean> => {
    try {
      const updated = await productService.adjustPricesByCategory(categoryId, percentage, operation);
      if (updated) {
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
      }
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleBulkUpdate = async (payload: UpdateProductBulkRequest): Promise<number> => {
    const result = await productService.bulkUpdate(payload);
    if (result.updatedCount > 0) {
      await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
    }
    return result.updatedCount;
  };

  const startNameEdit = (id: string, name: string) => { setEditingNameId(id); setDraftName(name); };
  const cancelNameEdit = () => { setEditingNameId(null); setDraftName(""); };
  const handleSaveName = async (id: string) => {
    if (!draftName.trim()) { toast.error("Nome inválido."); return; }
    setSavingNameId(id);
    try {
      const updated = await productService.updateName(id, draftName.trim());
      if (updated) {
        toast.success("Nome atualizado.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        cancelNameEdit();
      } else {
        toast.error("Não foi possível atualizar o nome.");
      }
    } catch {
      toast.error("Não foi possível atualizar o nome.");
    } finally {
      setSavingNameId(null);
    }
  };

  const startPriceEdit = (id: string, price: number) => {
    setEditingPriceId(id);
    setDraftPrice(price);
    setDraftPriceText(formatPriceBRL(price));
  };

  const cancelPriceEdit = () => {
    setEditingPriceId(null);
    setDraftPrice(null);
    setDraftPriceText("");
  };

  const startPlusPriceEdit = (id: string, plusPrice: number | null) => {
    setEditingPlusPriceId(id);
    setDraftPlusPrice(plusPrice);
    setDraftPlusPriceText(plusPrice != null ? formatPriceBRL(plusPrice) : "");
  };

  const cancelPlusPriceEdit = () => {
    setEditingPlusPriceId(null);
    setDraftPlusPrice(null);
    setDraftPlusPriceText("");
  };

  const handlePlusPriceChange = (val: string) => {
    if (val === "") {
      setDraftPlusPrice(null);
      setDraftPlusPriceText("");
      return;
    }
    const parsed = parseCurrencyInput(val);
    if (parsed !== null) {
      setDraftPlusPrice(parsed);
      setDraftPlusPriceText(formatPriceBRL(parsed));
    } else {
      setDraftPlusPrice(null);
      setDraftPlusPriceText(val);
    }
  };

  const handleSavePlusPrice = async (id: string) => {
    setSavingPlusPriceId(id);
    try {
      const updated = await productService.updatePlusPrice(id, draftPlusPrice);
      if (updated) {
        toast.success("Preço Plus atualizado.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        cancelPlusPriceEdit();
      } else {
        toast.error("Não foi possível atualizar o Preço Plus.");
      }
    } catch {
      toast.error("Não foi possível atualizar o Preço Plus.");
    } finally {
      setSavingPlusPriceId(null);
    }
  };

  const handlePriceChange = (val: string) => {
    const parsed = parseCurrencyInput(val); // number | null
    if (val === "") {
      setDraftPrice(null);
      setDraftPriceText("");
      return;
    }
    if (parsed !== null) {
      setDraftPrice(parsed);
      setDraftPriceText(formatPriceBRL(parsed));
    } else {
      // keep the raw text if parsing failed
      setDraftPrice(null);
      setDraftPriceText(val);
    }
  };

  const handleSavePrice = async (id: string) => {
    if (draftPrice === null || draftPrice < 0) {
      toast.error("Preço inválido.");
      return;
    }

    setSavingPriceId(id);
    try {
      const updated = await productService.updatePrice(id, draftPrice);
      if (updated) {
        toast.success("Preço atualizado.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        cancelPriceEdit();
      } else {
        toast.error("Não foi possível atualizar o preço.");
      }
    } catch {
      toast.error("Não foi possível atualizar o preço.");
    } finally {
      setSavingPriceId(null);
    }
  };

  const startStockEdit = (id: string, qty: number) => {
    setEditingStockId(id);
    setDraftStock(qty.toString());
  };

  const handleDeleteProduct = async () => {
    if (deletingId || !deleteTarget) {
      return;
    }

    setDeletingId(deleteTarget.id);
    try {
      const deleted = await productService.delete(deleteTarget.id);
      if (deleted) {
        toast.success("Produto excluído com sucesso.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        setDeleteTarget(null);
      } else {
        toast.error("Não foi possível excluir o produto (vinculado a pedidos).");
      }
    } catch {
      toast.error("Não foi possível excluir o produto.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDuplicate = async (id: string, idx: number) => {
    setDuplicatingId(id);
    try {
      const copy = await productService.duplicate(id);
      toast.success("Produto duplicado com sucesso.");
      setProducts(prev => {
        const next = [...prev];
        next.splice(idx + 1, 0, copy);
        return next;
      });
      setNewlyAddedId(copy.id);
      setTimeout(() => setNewlyAddedId(null), 700);
    } catch {
      toast.error("Não foi possível duplicar o produto.");
    } finally {
      setDuplicatingId(null);
    }
  };

  const cancelStockEdit = () => {
    setEditingStockId(null);
    setDraftStock("");
  };

  const handleSaveStock = async (id: string) => {
    const trimmed = draftStock.trim();
    if (!trimmed) {
      toast.error("Informe a quantidade em estoque.");
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error("Quantidade inválida.");
      return;
    }

    setSavingStockId(id);
    try {
      const updated = await productService.updateStock(id, parsed);
      if (updated) {
        toast.success("Estoque atualizado.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        cancelStockEdit();
      } else {
        toast.error("Não foi possível atualizar o estoque.");
      }
    } catch (error) {
      toast.error("Não foi possível atualizar o estoque.");
    } finally {
      setSavingStockId(null);
    }
  };

  const cancelCategoryEdit = () => {
    setEditingCategoryId(null);
    setDraftCategoryId("");
  };

  const handleSaveCategory = async (id: string) => {
    if (!draftCategoryId) {
      toast.error("Categoria inválida.");
      return;
    }

    setSavingCategoryId(id);
    try {
      const updated = await productService.updateCategory(id, draftCategoryId);
      if (updated) {
        toast.success("Categoria atualizada.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        cancelCategoryEdit();
      } else {
        toast.error("Não foi possível atualizar a categoria.");
      }
    } catch {
      toast.error("Não foi possível atualizar a categoria.");
    } finally {
      setSavingCategoryId(null);
    }
  };

  const cancelBrandEdit = () => {
    setEditingBrandId(null);
    setDraftBrandId("");
  };

  const handleSaveBrand = async (id: string) => {
    if (!draftBrandId) {
      toast.error("Marca inválida.");
      return;
    }

    setSavingBrandId(id);
    try {
      const updated = await productService.updateBrand(id, draftBrandId);
      if (updated) {
        toast.success("Marca atualizada.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        cancelBrandEdit();
      } else {
        toast.error("Não foi possível atualizar a marca.");
      }
    } catch {
      toast.error("Não foi possível atualizar a marca.");
    } finally {
      setSavingBrandId(null);
    }
  };

  const cancelModelEdit = () => {
    setEditingModelId(null);
    setDraftModelId("");
  };

  const handleSaveModel = async (id: string) => {
    setSavingModelId(id);
    try {
      const updated = await productService.updateModel(id, draftModelId || null);
      if (updated) {
        toast.success("Modelo atualizado.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        cancelModelEdit();
      } else {
        toast.error("Não foi possível atualizar o modelo.");
      }
    } catch {
      toast.error("Não foi possível atualizar o modelo.");
    } finally {
      setSavingModelId(null);
    }
  };

  const handleSaveCode = async (id: string) => {
    setSavingId(id);
    try {
      const updated = await productService.updateCode(id, draftCode.trim());
      if (updated) {
        toast.success("Código atualizado.");
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        setEditingId(null);
      } else {
        toast.error("Não foi possível atualizar o código.");
      }
    } catch (error: any) {
      const message = error?.response?.data?.message ?? "Não foi possível atualizar o código.";
      toast.error(message);
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleActive = async (id: string, current: boolean) => {
    try {
      const updated = await productService.updateFlags(id, { isActive: !current });
      if (updated) {
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        toast.success("Status do produto atualizado.");
      } else {
        toast.error("Não foi possível atualizar o status do produto.");
      }
    } catch {
      toast.error("Não foi possível atualizar o status do produto.");
    }
  };

  const handleToggleMadeToOrder = async (id: string, current: boolean) => {
    try {
      const updated = await productService.updateFlags(id, { isMadeToOrder: !current });
      if (updated) {
        await loadProducts(currentPage, selectedCategoryId, selectedBrandId, selectedModelId, status, search);
        toast.success("Flag de sob encomenda atualizada.");
      } else {
        toast.error("Não foi possível atualizar a flag de sob encomenda.");
      }
    } catch {
      toast.error("Não foi possível atualizar a flag de sob encomenda.");
    }
  };

  const isInitialLoading = isLoadingProducts && products.length === 0;

  return (
    <div className="min-h-screen flex flex-col">
      <SmartNavbar />
      <div className={`p-4 sm:p-14 max-w-full flex-1 flex flex-col`}>
        <div className="flex items-center justify-between gap-3 my-6 sm:my-0 sm:mb-8">
          <PageTitle>PRODUTOS</PageTitle>
          {isInitialLoading ? (
            <div className="flex items-center gap-3">
              <div className="hidden sm:block h-4 w-28 bg-gray-200 rounded animate-pulse" />
              <div className="h-9 w-28 bg-gray-200 rounded animate-pulse" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-sm text-gray-600">
                {lastUpdated ? `Atualizado às ${formatDateTimeToBR(lastUpdated)}` : "Carregando..."}
              </div>
              <Button variant="secondary"
                onClick={refreshProducts}
                disabled={isLoadingProducts || isRefreshing}
                className="px-3 py-2 text-sm"
              >
                Recarregar
              </Button>
            </div>
          )}
        </div>
        {totalItems > 0 && (<>
        <details className={`border border-gray-300 rounded p-3`}>
          {/* FILTER PANEL (shared mobile + desktop) */}
          <summary className="font-medium cursor-pointer">FILTROS</summary>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* CATEGORY */}
            {enableCategoriesFeature && (
            <div>
              <h3 className="text-sm font-semibold mb-2">CATEGORIAS</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSelectCategory(null)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    selectedCategoryId === null
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                  }`}
                >
                  TODOS OS PRODUTOS
                </button>

                {categories.map(category => (
                  <button
                    key={category.id}
                    onClick={() => handleSelectCategory(category.id)}
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${
                      selectedCategoryId === category.id
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* BRAND */}
            {enableBrandsFeature && (
            <div>
              <h3 className="text-sm font-semibold mb-2">MARCAS</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSelectBrand(null)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    selectedBrandId === null
                      ? "bg-primary-600 text-white border-primary-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                  }`}
                >
                  TODAS
                </button>

                {brands.map(brand => (
                  <button
                    key={brand.id}
                    onClick={() => handleSelectBrand(brand.id)}
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${
                      selectedBrandId === brand.id
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                    }`}
                  >
                    {brand.name}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* MODELOS */}
            {enableModelsFeature && models.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">MODELOS</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleSelectModel(null)}
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${
                      selectedModelId === null
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                    }`}
                  >
                    TODOS
                  </button>

                  {models.map(model => (
                    <button
                      key={model.id}
                      onClick={() => handleSelectModel(model.id)}
                      className={`px-3 py-1 rounded-full text-sm font-medium border ${
                        selectedModelId === model.id
                          ? "bg-primary-600 text-white border-primary-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                      }`}
                    >
                      {model.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STATUS */}
            <div>
              <h3 className="text-sm font-semibold mb-2">STATUS</h3>
              <div className="flex flex-wrap gap-2">
                {["all", "active", "inactive"].map(v => (
                  <button
                    key={v}
                    onClick={() => handleSelectStatus(v as any)}
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${
                      status === v
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                    }`}
                  >
                    {v === "all" ? "TODOS" : v === "active" ? "ATIVOS" : "INATIVOS"}
                  </button>
                ))}
              </div>
            </div>

          </div>
        </details>
        </>)}

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between my-4 gap-4">
          {totalItems > 0 && (
          <div className="w-full sm:flex-1 sm:max-w-2xl">
            <h3 className="text-sm font-semibold mb-2">PESQUISA</h3>
            <SearchBar
              onSearch={handleSearch}
              value={search}
              className="w-full py-3"
              placeholder="Buscar por nome ou código do produto..."
              borderClass="border-gray-300"
              iconClass="text-primary-600"
            />
          </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-y-2 sm:gap-x-4">
            {totalItems > 0 && (
            <button
              onClick={() => setAdjustPricesModalOpen(true)}
              className="uppercase sm:normal-case flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700"
            >
              <FaPercentage /> Ajustar preços
            </button>
            )}

            {totalItems > 0 && (
            <button
              onClick={() => setIsBulkUpdateModalOpen(true)}
              className="uppercase sm:normal-case flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700"
            >
              <FaEdit /> Atualizar produtos
            </button>
            )}

            {totalItems > 0 && (
            <button
              onClick={handleOpenProductAnalysis}
              className="uppercase sm:normal-case flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700"
            >
              <FaSearch /> Analisar produtos
            </button>
            )}

            {!isInitialLoading && (
            <button
              className="uppercase sm:normal-case flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700 sm:col-start-1 sm:row-start-2"
              onClick={() => setDetailsModalId("novo")}
            >
              <FaPlus /> Adicionar Produto
            </button>
            )}

            {!isInitialLoading && (
            <button
              className="uppercase sm:normal-case flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700 sm:col-start-2 sm:row-start-2"
              onClick={() => navigate("/admin/produtos/importar")}
            >
              <FaFileCsv /> Importar produtos
            </button>
            )}

            {totalItems > 0 && (
            <button
              className="uppercase sm:normal-case flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700 disabled:opacity-60 sm:col-start-3 sm:row-start-2"
              onClick={handleOpenReportModal}
              disabled={isGeneratingReport}
            >
              {isGeneratingReport ? <ButtonSpinner /> : <FaFileCsv />} {"Exportar relatório"}
            </button>
            )}
          </div>
        </div>

        {totalItems > 0 && (<>
        <div className="flex flex-col gap-4 sm:hidden">
          {/* MOBILE CARDS */}
          {products.map((p, index) => (
            <div key={p.id} className="border rounded p-4 shadow-sm bg-white">
              <h2 className="font-semibold mb-2">
                Produto {index + 1}
              </h2>
              <p>
                <span className="font-medium">Código:</span> {p.code}
              </p>
              <p>
                <span className="font-medium">Nome:</span> {p.name}
              </p>
              {!(tenantPreference?.enableProductComponents ?? true) && (
                <p>
                  <span className="font-medium">Tamanhos:</span> {formatSizesDisplay(p.availableSizes)}
                </p>
              )}
              <p>
                <span className="font-medium">{!(tenantPreference?.enableProductComponents ?? true) ? "Preço (P-GG):" : "Preço:"}</span> {formatPriceBRL(p.price)}
              </p>
              {!(tenantPreference?.enableProductComponents ?? true) && (
                <p>
                  <span className="font-medium">Preço (G1-G3):</span> {p.plusPrice != null ? formatPriceBRL(p.plusPrice) : "–"}
                </p>
              )}
              <p>
                <span className="font-medium">Estoque:</span> {p.stockQuantity}
              </p>
              <p>
                <span className="font-medium">Ativo:</span> {p.isActive ? "Sim" : "Não"}
              </p>
              {enableMadeToOrderProducts && (
                <p>
                  <span className="font-medium">Sob encomenda:</span> {p.isMadeToOrder ? "Sim" : "Não"}
                </p>
              )}
              {enableCategoriesFeature && (
                <p>
                  <span className="font-medium">Categoria:</span> {p.category?.name ?? "Sem categoria"}
                </p>
              )}
              {enableBrandsFeature && (
                <p>
                  <span className="font-medium">Marca:</span> {p.brand?.name ?? "Sem marca"}
                </p>
              )}
              <div className="grid grid-cols-3 gap-2 mt-3">
                <Button
                  onClick={() => setViewModalId(p.id)}
                  className="flex items-center justify-center gap-1.5 py-2 text-sm"
                >
                  <FaEye /> {"Visualizar"}
                </Button>
                <Button
                  onClick={() => setDetailsModalId(p.id)}
                  className="flex items-center justify-center gap-1.5 py-2 text-sm"
                >
                  <FaEdit /> {"Editar"}
                </Button>
                <Button
                  onClick={() => setFilesModalId(p.id)}
                  className="flex items-center justify-center gap-1.5 py-2 text-sm"
                  title="Acessar imagens e documentos"
                >
                  <FaImages /> {"Arquivos"}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                  onClick={() => handleDuplicate(p.id, index)}
                  disabled={duplicatingId === p.id}
                  className="flex items-center justify-center gap-1.5 py-2 text-sm"
                  title="Duplicar produto"
                >
                  <FaCopy /> {"Duplicar"}
                </Button>
                {p.canDelete ? (
                  <Button
                    onClick={() => setDeleteTarget({ id: p.id, name: p.name })}
                    disabled={deletingId === p.id}
                    className="flex items-center justify-center gap-1.5 py-2 text-sm"
                    title={"Excluir produto"}
                  >
                    <FaTrash /> {"Excluir"}
                  </Button>
                ) : (
                  <BlockedAction
                    label={"Excluir"}
                    reason={getDeleteBlockedReason(p)}
                    className="flex items-center justify-center gap-1.5 py-2 text-sm"
                  >
                    <FaTrash /> {"Excluir"}
                  </BlockedAction>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* DESKTOP TABLE */}
        <table className={`hidden sm:table w-full border-collapse border border-gray-300 table-fixed`}>
          <thead className="bg-primary-120">
            <tr>
              <th className={`border p-3 text-center ${isCodeEditing ? 'w-[6.5rem]' : 'w-20'}`}>
                Código
              </th>
              <th className={`border p-3 text-left truncate ${!(tenantPreference?.enableProductComponents ?? true) ? 'w-44' : 'w-36'}`}>
                Nome
              </th>
              {!(tenantPreference?.enableProductComponents ?? true) && (
                <th className="border p-3 text-left w-24">
                  {"Tamanhos"}
                </th>
              )}
              <th className={`border p-3 text-left ${isPriceEditing ? 'w-[7.5rem]' : !(tenantPreference?.enableProductComponents ?? true) ? 'w-20' : 'w-24'}`}>
                {!(tenantPreference?.enableProductComponents ?? true) ? "P–GG" : "Preço"}
              </th>
              {!(tenantPreference?.enableProductComponents ?? true) && (
                <th className={`border p-3 text-left ${isPlusPriceEditing ? 'w-[7.5rem]' : 'w-20'}`}>
                  {"G1–G3"}
                </th>
              )}
              <th className={`border p-3 text-left ${isStockEditing ? 'w-[5.5rem]' : 'w-14'}`}>
                Estoque
              </th>
              <th className="border p-3 text-center w-11">
                Ativo
              </th>
              {enableMadeToOrderProducts && (
                <th className="border p-3 text-center w-14">
                  Sob enc.
                </th>
              )}
              {enableCategoriesFeature && (
                <th className={`border p-3 text-left ${isCategoryEditing ? 'w-[9.5rem]' : 'w-24'}`}>
                  Categoria
                </th>
              )}
              {enableBrandsFeature && (
                <th className={`border p-3 text-left ${isBrandEditing ? 'w-[9.5rem]' : 'w-20'}`}>
                  Marca
                </th>
              )}
              {enableModelsFeature && (
                <th className={`border p-3 text-left ${isModelEditing ? 'w-[9.5rem]' : 'w-20'}`}>
                  Modelo
                </th>
              )}
              <th className="border p-3 text-center w-20">
                Ações
              </th>
            </tr>
          </thead>
          {isLoadingProducts ? (
            <tbody>
              {Array.from({ length: 6 }).map((_, index) => (
                <tr key={index} className="animate-pulse">
                  {Array.from({ length: 9 }).map((__, cellIndex) => (
                    <td key={cellIndex} className="border p-3">
                      <div className="h-4 bg-gray-200 rounded w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              {products.map((c, idx) => {
                return (
                  <tr key={c.id} className={`hover:bg-gray-50 ${newlyAddedId === c.id ? "row-slide-in" : ""}`}>
                    <td className="border p-3 text-center">
                      {editingId === c.id ? (
                        <div className="flex items-center gap-1 justify-between w-full">
                          <Input
                            className="border rounded w-full max-w-[7.5rem]"
                            value={draftCode}
                            onChange={(e) => setDraftCode(e.target.value)}
                            disabled={savingId === c.id}
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleSaveCode(c.id)}
                              disabled={savingId === c.id}
                              className="text-green-600"
                              title="Salvar"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              disabled={savingId === c.id}
                              className="text-red-600"
                              title="Cancelar"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{c.code}</span>
                          <button
                            onClick={() => { setEditingId(c.id); setDraftCode(c.code); }}
                            className="text-primary-600 shrink-0"
                            title="Editar código"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="border p-3">
                      {editingNameId === c.id ? (
                        <div className="flex items-center gap-1 justify-between w-full">
                          <input
                            className="border rounded px-2 py-1 w-full"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            disabled={savingNameId === c.id}
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => handleSaveName(c.id)} disabled={savingNameId === c.id} className="text-green-600" title="Salvar"><FaCheck /></button>
                            <button onClick={cancelNameEdit} disabled={savingNameId === c.id} className="text-red-600" title="Cancelar"><FaTimes /></button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{c.name}</span>
                          <button onClick={() => startNameEdit(c.id, c.name)} className="text-primary-600 shrink-0" title="Editar nome"><FaEdit /></button>
                        </div>
                      )}
                    </td>
                    {!(tenantPreference?.enableProductComponents ?? true) && (
                      <td className="border p-3 w-24">
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{formatSizesDisplay(c.availableSizes)}</span>
                          <button
                            onClick={() => { setSizesModalProductId(c.id); setDraftSizes(c.availableSizes?.length ? [...c.availableSizes] : [...ALL_SIZES]); }}
                            className="text-primary-600 shrink-0"
                            title="Editar tamanhos"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="border p-3">
                      {editingPriceId === c.id ? (
                        <div className="flex items-center gap-1 justify-between w-full">
                          <input
                            type="text"
                            className="border rounded px-2 py-1 w-full max-w-[8rem]"
                            value={draftPriceText}
                            onChange={(e) => handlePriceChange(e.target.value)}
                            disabled={savingPriceId === c.id}
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => handleSavePrice(c.id)} disabled={savingPriceId === c.id} className="text-green-600" title="Salvar">
                              <FaCheck />
                            </button>
                            <button onClick={cancelPriceEdit} disabled={savingPriceId === c.id} className="text-red-600" title="Cancelar">
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{formatPriceBRL(c.price)}</span>
                          <button
                            onClick={() => startPriceEdit(c.id, c.price)}
                            className="text-primary-600 shrink-0"
                            title="Editar preço"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      )}
                    </td>
                    {!(tenantPreference?.enableProductComponents ?? true) && (
                      <td className="border p-3">
                        {editingPlusPriceId === c.id ? (
                          <div className="flex items-center gap-1 justify-between w-full">
                            <input
                              type="text"
                              className="border rounded px-2 py-1 w-full max-w-[8rem]"
                              value={draftPlusPriceText}
                              onChange={(e) => handlePlusPriceChange(e.target.value)}
                              disabled={savingPlusPriceId === c.id}
                            />
                            <div className="flex items-center gap-1 shrink-0">
                              <button onClick={() => handleSavePlusPrice(c.id)} disabled={savingPlusPriceId === c.id} className="text-green-600" title="Salvar">
                                <FaCheck />
                              </button>
                              <button onClick={cancelPlusPriceEdit} disabled={savingPlusPriceId === c.id} className="text-red-600" title="Cancelar">
                                <FaTimes />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 justify-between w-full">
                            {c.plusPrice != null ? (
                              <span className="truncate flex-1 text-left">{formatPriceBRL(c.plusPrice)}</span>
                            ) : (
                              <span className="text-gray-400 flex-1">{"—"}</span>
                            )}
                            <button
                              onClick={() => startPlusPriceEdit(c.id, c.plusPrice ?? null)}
                              className="text-primary-600 shrink-0"
                              title="Editar preço plus"
                            >
                              <FaEdit />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                    <td className={`border p-3 ${isStockEditing ? 'w-[5.5rem]' : 'w-14'}`}>
                      {editingStockId === c.id ? (
                        <div className="flex items-center gap-1 justify-between w-full">
                          <Input
                            type="number"
                            min={0}
                            className="border rounded px-2 py-1 w-full max-w-[6rem]"
                            value={draftStock}
                            onChange={(e) => setDraftStock(e.target.value)}
                            disabled={savingStockId === c.id}
                          />
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleSaveStock(c.id)}
                              disabled={savingStockId === c.id}
                              className="text-green-600"
                              title="Salvar"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={cancelStockEdit}
                              disabled={savingStockId === c.id}
                              className="text-red-600"
                              title="Cancelar"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{c.stockQuantity}</span>
                          <button
                            onClick={() => startStockEdit(c.id, c.stockQuantity)}
                            className="text-primary-600 shrink-0"
                            title="Editar estoque"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="border p-3 text-center">
                      <button
                        className="inline-flex justify-center items-center mx-auto"
                        onClick={() => handleToggleActive(c.id, c.isActive)}
                      >
                        {c.isActive ? (
                          <FaCheckCircle className="text-green-600" />
                        ) : (
                          <FaTimesCircle className="text-red-600" />
                        )}
                      </button>
                    </td>
                    {enableMadeToOrderProducts && (
                      <td className="border p-3 text-center">
                        <button
                          className="inline-flex justify-center items-center mx-auto"
                          onClick={() => handleToggleMadeToOrder(c.id, c.isMadeToOrder)}
                        >
                          {c.isMadeToOrder ? (
                            <FaCheckCircle className="text-green-600" />
                          ) : (
                            <FaTimesCircle className="text-red-600" />
                          )}
                        </button>
                      </td>
                    )}
                    {enableCategoriesFeature && (
                    <td className="border p-3 truncate">
                      {editingCategoryId === c.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            className="border rounded px-2 py-1 w-full"
                            value={draftCategoryId}
                            onChange={(e) => setDraftCategoryId(e.target.value)}
                            disabled={savingCategoryId === c.id}
                          >
                            <option value="">Selecione</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleSaveCategory(c.id)}
                              disabled={savingCategoryId === c.id}
                              className="text-green-600"
                              title="Salvar"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={cancelCategoryEdit}
                              disabled={savingCategoryId === c.id}
                              className="text-red-600"
                              title="Cancelar"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{c.category?.name ?? "Sem categoria"}</span>
                          <button
                            onClick={() => { setEditingCategoryId(c.id); setDraftCategoryId(c.category?.id ?? ""); }}
                            className="text-primary-600 shrink-0"
                            title="Editar categoria"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      )}
                    </td>
                    )}
                    {enableBrandsFeature && (
                    <td className="border p-3 truncate">
                      {editingBrandId === c.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            className="border rounded px-2 py-1 w-full"
                            value={draftBrandId}
                            onChange={(e) => setDraftBrandId(e.target.value)}
                            disabled={savingBrandId === c.id}
                          >
                            <option value="">Selecione</option>
                            {brands.map((brand) => (
                              <option key={brand.id} value={brand.id}>
                                {brand.name}
                              </option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleSaveBrand(c.id)}
                              disabled={savingBrandId === c.id}
                              className="text-green-600"
                              title="Salvar"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={cancelBrandEdit}
                              disabled={savingBrandId === c.id}
                              className="text-red-600"
                              title="Cancelar"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{c.brand?.name ?? "Sem marca"}</span>
                          <button
                            onClick={() => { setEditingBrandId(c.id); setDraftBrandId(c.brand?.id ?? ""); }}
                            className="text-primary-600 shrink-0"
                            title="Editar marca"
                          >
                            <FaEdit />
                          </button>
                        </div>
                      )}
                    </td>
                    )}
                    {enableModelsFeature && (
                    <td className="border p-3 truncate">
                      {editingModelId === c.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            className="border rounded px-2 py-1 w-full"
                            value={draftModelId}
                            onChange={(e) => setDraftModelId(e.target.value)}
                            disabled={savingModelId === c.id}
                          >
                            <option value="">SEM MODELO</option>
                            {models.map((model) => (
                              <option key={model.id} value={model.id}>
                                {model.name}
                              </option>
                            ))}
                          </select>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => handleSaveModel(c.id)}
                              disabled={savingModelId === c.id}
                              className="text-green-600"
                              title="Salvar"
                            >
                              <FaCheck />
                            </button>
                            <button
                              onClick={cancelModelEdit}
                              disabled={savingModelId === c.id}
                              className="text-red-600"
                              title="Cancelar"
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-between w-full">
                          <span className="truncate flex-1 text-left">{c.model?.name ?? "N/I"}</span>
                            <button
                              onClick={() => { setEditingModelId(c.id); setDraftModelId(c.model?.id ?? ""); }}
                              className="text-primary-600 shrink-0"
                              title="Editar modelo"
                            >
                              <FaEdit />
                          </button>
                        </div>
                      )}
                    </td>
                    )}
                    <td className="border p-3 text-center truncate">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => setViewModalId(c.id)}
                          className="flex justify-center items-center gap-1 text-primary-600 hover:underline"
                          title="Visualizar produto"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => setDetailsModalId(c.id)}
                          className="flex justify-center items-center gap-1 text-primary-600 hover:underline"
                          title="Editar produto"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => setFilesModalId(c.id)}
                          className="flex justify-center items-center gap-1 text-primary-600 hover:underline"
                          title="Acessar imagens e documentos"
                        >
                          <FaImages />
                        </button>
                        <button
                          onClick={() => handleDuplicate(c.id, idx)}
                          disabled={duplicatingId === c.id}
                          className="text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Duplicar produto"
                        >
                          <FaCopy />
                        </button>
                        {c.canDelete ? (
                          <button
                            onClick={() => setDeleteTarget({ id: c.id, name: c.name })}
                            disabled={deletingId === c.id}
                            className="text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Excluir produto"
                          >
                            <FaTrash />
                          </button>
                        ) : (
                          <BlockedAction label={"Excluir"} reason={getDeleteBlockedReason(c)} className="text-gray-300" plain>
                            <FaTrash />
                          </BlockedAction>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
        </>)}

        {!isLoadingProducts && totalItems === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
            <FaBoxOpen className="text-3xl" />
            <p className="text-center text-sm sm:text-base">Nenhum produto encontrado</p>
          </div>
        ) : isLoadingProducts ? (
          <div className="flex flex-col items-center mt-10 gap-4">
            <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
            <div className="flex gap-2">
              {Array.from({ length: 7 }).map((_, index) => (
                <div key={index} className="w-10 h-10 bg-gray-200 rounded animate-pulse" />
              ))}
            </div>
          </div>
        ) : (
          <>
            {products.length > 0 && (
              <>
                <PaginationSummary
                  totalItems={totalItems}
                  singularLabel="produto encontrado"
                  pluralLabel="produtos encontrados"
                />
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      <EditProductSizesModal
        isOpen={!!sizesModalProductId && !!editingSizesProduct}
        productName={editingSizesProduct?.name ?? ""}
        selectedSizes={draftSizes}
        isSaving={savingSizes}
        onToggle={sz => setDraftSizes(prev => prev.includes(sz) ? prev.filter(s => s !== sz) : [...prev, sz])}
        onSave={handleSaveSizes}
        onClose={() => setSizesModalProductId(null)}
      />
      <AdjustPricesModal
        isOpen={isAdjustPricesModalOpen}
        onClose={() => setAdjustPricesModalOpen(false)}
        categories={categories}
        onConfirm={handleAdjustPrices}
      />
      <BulkUpdateProductsModal
        isOpen={isBulkUpdateModalOpen}
        onClose={() => setIsBulkUpdateModalOpen(false)}
        categories={categories}
        brands={brands}
        onConfirm={handleBulkUpdate}
      />
      <ProductAnalysisModal
        isOpen={isProductAnalysisModalOpen}
        onClose={() => setIsProductAnalysisModalOpen(false)}
        data={productAnalysis}
        isLoading={isAnalyzingProducts}
      />
      <DeleteProductModal
        isOpen={!!deleteTarget}
        productName={deleteTarget?.name}
        onClose={() => {
          if (!deletingId) {
            setDeleteTarget(null);
          }
        }}
        onConfirm={handleDeleteProduct}
        loading={!!deletingId}
      />
      <ReportFormatModal
        isOpen={isReportFormatModalOpen}
        onClose={() => setIsReportFormatModalOpen(false)}
        onSelect={handleSelectReportFormat}
      />
      <AdminProductDetailsModal
        productId={detailsModalId}
        onClose={() => setDetailsModalId(null)}
        onSaved={refreshProducts}
      />
      <AdminProductFilesModal
        productId={filesModalId}
        onClose={() => setFilesModalId(null)}
      />
      <AdminProductViewModal
        productId={viewModalId}
        onClose={() => setViewModalId(null)}
        onEdit={(id) => { setViewModalId(null); setDetailsModalId(id); }}
      />
    </div>
  );
};

export default AdminProductPage;
